import { useEffect, useState } from "react";
import { publicApi, type Incident } from "../lib/api";
import { stripMarkdown } from "../lib/markdown";

function formatWhen(iso: string | undefined): {
  local: string;
  utc: string;
} {
  if (!iso) return { local: "", utc: "" };
  try {
    const dt = new Date(iso);
    const local = dt.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    const utc = dt.toUTCString();
    return { local, utc };
  } catch {
    return { local: iso, utc: iso };
  }
}

/** Upcoming maintenance section. Hidden when there is nothing scheduled. */
export function UpcomingMaintenance() {
  const [incidents, setIncidents] = useState<Incident[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await publicApi.upcomingMaintenance();
        if (!cancelled) {
          setIncidents(resp.incidents);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "unknown");
        }
      }
    }
    void load();
    const id = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Empty list OR fetch error -> render nothing. A status page should not
  // advertise "no maintenance scheduled"; silence is the right default.
  if (error || !incidents || incidents.length === 0) return null;

  return (
    <section
      aria-labelledby="upcoming-maintenance-heading"
      className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4"
    >
      <h2
        id="upcoming-maintenance-heading"
        className="text-sm font-semibold uppercase tracking-wider text-indigo-900"
      >
        Upcoming maintenance
      </h2>
      <ol className="mt-3 space-y-3">
        {incidents.map((inc) => {
          const when = formatWhen(inc.scheduled_for);
          // Excerpt is plain text, so strip the syntax rather than show it.
          const bodyFirstLine = stripMarkdown(inc.updates?.[0]?.body_markdown)
            .trim()
            .split("\n")[0]
            .slice(0, 280);
          return (
            <li
              key={inc.incident_id}
              className="rounded-md bg-white p-3 ring-1 ring-indigo-100"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900">{inc.title}</p>
                  {inc.affected_components.length > 0 && (
                    <p className="mt-0.5 text-xs uppercase tracking-wide text-gray-500">
                      Affects: {inc.affected_components.join(", ")}
                    </p>
                  )}
                  {bodyFirstLine && (
                    <p className="mt-1 text-sm text-gray-700">{bodyFirstLine}</p>
                  )}
                </div>
                <time
                  title={when.utc}
                  dateTime={inc.scheduled_for}
                  className="shrink-0 whitespace-nowrap text-sm font-medium text-indigo-900"
                >
                  {when.local}
                </time>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
